import { Roles } from "../../../../../constants/roles";

import { FormBase } from "../../../../../util/form-base-form";

export interface CategoryEditorFormElements {
    tabParentEl: HTMLElement | null;
    tabChildEl: HTMLElement | null;
    tabReviewEl: HTMLElement | null;
    panelParentEl: HTMLElement | null;
    panelChildEl: HTMLElement | null;
    panelReviewEl: HTMLElement | null;
    saveBtnEl: HTMLElement | null;
    addBtnEl: HTMLElement | null;
    statusEl: HTMLElement | null;
    addEntryModalEl: HTMLElement | null;
    addEntryCodeEl: HTMLInputElement | null;
    addEntryNameEl: HTMLInputElement | null;
    addEntrySubmitBtnEl: HTMLElement | null;
    addEntryCloseBtnEls: HTMLElement[];
    questionOptionModalEl: HTMLElement | null;
    questionOptionRowsEl: HTMLElement | null;
    questionOptionAddRowBtnEl: HTMLElement | null;
    questionOptionSubmitBtnEl: HTMLElement | null;
    questionOptionCloseBtnEls: HTMLElement[];
    closeBtnEls: HTMLElement[];
    openBtnEls: HTMLElement[];
}

export class CategoryEditorForm extends FormBase {
    public rootEl: Document | HTMLElement;
    public modalEl: HTMLElement;

    /**
     * @param {Document|HTMLElement} rootEl
     * @param {HTMLElement} modalEl
     */
    public constructor(rootEl: Document | HTMLElement, modalEl: HTMLElement) {
        super();
        this.rootEl = rootEl;
        this.modalEl = modalEl;
    }

    public getElements(): CategoryEditorFormElements {
        const tabParentEl = this.queryElement(
            this.modalEl,
            `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_TAB_CATEGORY}"]`,
        );
        const tabChildEl = this.queryElement(
            this.modalEl,
            `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_TAB_QUESTION}"]`,
        );
        const tabReviewEl = this.queryElement(this.modalEl, `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_TAB_REVIEW}"]`);

        const panelParentEl = this.queryElement(
            this.modalEl,
            `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_PANEL_CATEGORY}"]`,
        );
        const panelChildEl = this.queryElement(
            this.modalEl,
            `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_PANEL_QUESTION}"]`,
        );
        const panelReviewEl = this.queryElement(
            this.modalEl,
            `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_PANEL_REVIEW}"]`,
        );

        const saveBtnEl = this.queryElement(this.modalEl, `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_SAVE}"]`);
        const addBtnEl = this.queryElement(this.modalEl, '[data-action="open-add-category"]');
        const statusEl = this.queryElement(this.modalEl, `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_STATUS}"]`);

        const addEntryModalEl = this.queryElement(this.modalEl, '[data-action="add-entry-modal"]');
        const addEntryCodeEl = this.getInput(this.modalEl, '[data-action="add-entry-code"]');
        const addEntryNameEl = this.getInput(this.modalEl, '[data-action="add-entry-name"]');
        const addEntrySubmitBtnEl = this.queryElement(this.modalEl, '[data-action="add-entry-submit"]');
        const addEntryCloseBtnEls = this.getAddEntryCloseButtons();

        const questionOptionModalEl = this.queryElement(this.modalEl, '[data-action="question-option-modal"]');
        const questionOptionRowsEl = this.queryElement(this.modalEl, '[data-action="question-option-rows"]');
        const questionOptionAddRowBtnEl = this.queryElement(this.modalEl, '[data-action="question-option-add-row"]');
        const questionOptionSubmitBtnEl = this.queryElement(this.modalEl, '[data-action="question-option-submit"]');
        const questionOptionCloseBtnEls = this.getQuestionOptionCloseButtons();

        const closeBtnEls = this.getCloseButtons();

        const openBtnEls = this.getOpenButtons();

        return {
            tabParentEl,
            tabChildEl,
            tabReviewEl,
            panelParentEl,
            panelChildEl,
            panelReviewEl,
            saveBtnEl,
            addBtnEl,
            statusEl,
            addEntryModalEl,
            addEntryCodeEl,
            addEntryNameEl,
            addEntrySubmitBtnEl,
            addEntryCloseBtnEls,
            questionOptionModalEl,
            questionOptionRowsEl,
            questionOptionAddRowBtnEl,
            questionOptionSubmitBtnEl,
            questionOptionCloseBtnEls,
            closeBtnEls,
            openBtnEls,
        };
    }

    public getAddEntryCloseButtons(): HTMLElement[] {
        return this.queryElements(this.modalEl, '[data-action="add-entry-close"]', HTMLElement);
    }

    public getQuestionOptionCloseButtons(): HTMLElement[] {
        return this.queryElements(this.modalEl, '[data-action="question-option-close"]', HTMLElement);
    }

    public getCloseButtons(): HTMLElement[] {
        return this.queryElements(this.modalEl, `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_CLOSE}"]`, HTMLElement);
    }

    public getOpenButtons(): HTMLElement[] {
        return this.queryElements(this.rootEl, `[data-role="${Roles.SEARCH_CATEGORY_MODAL_EDIT_OPEN}"]`, HTMLElement);
    }

    public getAddEntryTitle(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, '[data-action="add-entry-title"]', HTMLElement);
    }

    public getAddEntryCodePrefix(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, '[data-action="add-entry-code-prefix"]', HTMLElement);
    }

    public getAddEntryCodeSuffix(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, '[data-action="add-entry-code-suffix"]', HTMLElement);
    }

    public getQuestionOptionRows(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ".category-review-editor__option-row", HTMLElement);
    }

    public getQuestionOptionRowThInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, '[data-action="question-option-row-th"]');
    }

    public getQuestionOptionRowEnInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, '[data-action="question-option-row-en"]');
    }

    public getQuestionOptionRowJaInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, '[data-action="question-option-row-ja"]');
    }

    public getQuestionOptionAddRowButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="question-option-add-row"]', HTMLElement);
    }

    public getQuestionOptionRemoveRowButton(
        root: Event | EventTarget | Element | null | undefined,
    ): HTMLElement | null {
        return this.closest(root, '[data-action="question-option-remove-row"]', HTMLElement);
    }

    public getQuestionOptionRow(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, ".category-review-editor__option-row", HTMLElement);
    }

    public getQuestionOptionRowNo(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".category-review-editor__option-no", HTMLElement);
    }
}
